import React, { useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
  diagnosticReportViewOptions,
  TOTAL_MFD_CUMULATIVE,
  TOTAL_MFD_INCREMENTAL,
} from "../../constants/diagnosticReport";
import { SolutionDiagnosticsOption, MetaArguments } from "../../views/GeneralTask/generalTask.types";
import MultiSelect from "../common/MultiSelect";
import GeneralViewMfdDynamicDialog from "./GeneralViewMfdDynamicDialog";
import GeneralViewMfdStaticDialog from "./GeneralViewMfdStaticDialog";
import useLocalStorage from "@rehooks/local-storage";
import { MfdTableMeta } from "../common/MfdChart";
import CentredCircularProgress from "../common/CentredCircularProgress";

const StyledImageContainer = styled("div")(() => ({
  display: "flex",
  justifyContent: "center",
  flexWrap: "wrap",
  minHeight: "30vh",
}));

interface GeneralViewProps {
  id: string;
  mfdTableMeta: MfdTableMeta | undefined;
  filteredMeta: MetaArguments;
  currentImage: number;
  automationTasksLength: number;
  solutionType: string;
}

const GeneralView: React.FC<GeneralViewProps> = ({
  id,
  mfdTableMeta,
  filteredMeta,
  currentImage,
  automationTasksLength,
  solutionType,
}: GeneralViewProps) => {
  const [viewOptions, setViewOptions] = useLocalStorage<SolutionDiagnosticsOption[]>("viewOptions");
  const [generalViews, setGeneralViews] = useLocalStorage<string[]>("generalViews");
  const [generalViewSelections, setGeneralViewSelections] =
    useLocalStorage<SolutionDiagnosticsOption[]>("generalViewSelections");

  useEffect(() => {
    if (solutionType === "ScaledInversionSolution" || solutionType === "TimeDependentInversionSolution") {
      const scaledReportViewOptions = diagnosticReportViewOptions.filter(
        (option) =>
          option.displayName !== "Perturbations & Non-Zero Rates" &&
          option.displayName !== "Energy vs Time" &&
          option.displayName !== "Misfit Progress" &&
          option.displayName !== "Re-Weighting",
      );
      setViewOptions(scaledReportViewOptions);
    } else {
      setViewOptions(diagnosticReportViewOptions);
    }
  }, [solutionType]);

  useEffect(() => {
    const filtered = viewOptions?.filter((option) => generalViews?.includes(option?.displayName));
    setGeneralViewSelections(filtered || []);
  }, [viewOptions, generalViews]);

  const generalViewDisplayNames: string[] = [];

  viewOptions?.map((option) => {
    generalViewDisplayNames.push(option.displayName);
  });

  return (
    <div>
      <MultiSelect
        name="Reports"
        selected={generalViews || []}
        options={generalViewDisplayNames}
        setOptions={setGeneralViews}
      />
      <StyledImageContainer>
        {generalViewSelections?.length &&
          generalViewSelections?.map((option) => {
            const isTotalMfd = option.id === TOTAL_MFD_INCREMENTAL || option.id === TOTAL_MFD_CUMULATIVE;
            // Without a table there is nothing to plot, so fall through to the
            // static image rather than rendering an empty chart.
            if (isTotalMfd && solutionType === "InversionSolution" && mfdTableMeta?.id) {
              return (
                // The MFD table query lives inside the dialog, which needs its
                // rows before it can tell whether there is a chart to draw.
                <React.Suspense key={option.finalPath} fallback={<CentredCircularProgress />}>
                  <GeneralViewMfdDynamicDialog
                    id={id}
                    mfdTableMeta={mfdTableMeta}
                    option={option}
                    currentImage={currentImage}
                    automationTasksLength={automationTasksLength}
                    filteredMeta={filteredMeta}
                  />
                </React.Suspense>
              );
            } else {
              return (
                <GeneralViewMfdStaticDialog
                  key={option.finalPath}
                  id={id}
                  option={option}
                  currentImage={currentImage}
                  automationTasksLength={automationTasksLength}
                  filteredMeta={filteredMeta}
                />
              );
            }
          })}
      </StyledImageContainer>
    </div>
  );
};

export default GeneralView;
